/* eslint-disable indent */
import React, { useEffect, useReducer, useState } from 'react';
import { useParams } from 'react-router';
import * as store from './assessment-management.store';
import { PageHeader, Modal, Panel, Button, SearchBar, DropdownSelect, Filters, useMatchScreenWidth, AddTwoToneIcon, DropdownMenu } from '@keplerco/core';
import { EmptyState } from '../../components/general/empty-state/empty-state';
import { AssessmentSubType } from '../../enums/assessment-type';
import { ColumnConfiguration } from '../../models/column-configuration';
import { PagePath } from '../../navigation/navigation.enums';
import { useAppActions, useAppState } from '../../overmind';
import { ColumnConfiguratorWidget, ConfirmationModalLayout } from '../../widgets/layouts';
import { AssessmentManagementTable } from './assessment-management.table';
import { useKeplerNavigate } from '../../navigation/guards/use-kepler-navigate';
import { AssessmentStatus } from '../../enums/assessment-status';
import { AssessmentListItemResponse, AssessmentListSearchParams } from '../../overmind/actions';
import { SortByOption } from '../../enums/sort-by-option';
import { ASSESSMENT_SUB_TYPE_OPTIONS, assessmentSortByOptions } from '../../library/consts/consts';
import { FiltersOutlet } from '../analytics/analytics-filters/filters-outlet';
import { SelectedFilterOption } from '../analytics/analytics-filters/analytics-filters.models';
import { SortBy } from '../../components/general/sort-by/sort-by';
import { SkeletonOverlay } from '../../components/general/loading-state/skeleton-overlay';
import { FilterType, STATUS_OPTIONS } from './assessment-management.models';

export function AssessmentManagementPage(): JSX.Element {
  const params = useParams();
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [state, dispatch] = useReducer(store.reducer, store.initialState);

  const [loadingSkillAssessments, setLoadingSkillAssessments] = React.useState<string[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilterOption[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const isMobile = useMatchScreenWidth('mobile');

  async function updateData(searchParams: AssessmentListSearchParams) {
    const data = await actions.getAssessmentList(searchParams);
    dispatch({ type: store.AssessmentManagementActionType.SetData, payload: data });
  }

  useEffect(() => {
    async function initPage() {
      setIsLoading(true);

      const searchParams: AssessmentListSearchParams = {
        ...state.searchParams,
        companySlug: params.companySlug!,
        sortBy: SortByOption.MostRecentlyCreated,
        page: 1,
      };
      dispatch({ type: store.AssessmentManagementActionType.SetSearchParams, payload: searchParams });

      await updateData(searchParams);

      setIsLoading(false);
    }

    initPage();
  }, [companyVariables.slug]);

  async function onPageChangeHandler(page: number) {
    if (!!page && page === state.searchParams?.page) return;

    setIsLoading(true);
    dispatch({ type: store.AssessmentManagementActionType.SetSearchParams, payload: { ...state.searchParams, page } });
    await updateData({ ...state.searchParams, page });
    setIsLoading(false);
  }

  async function onClickManageAssessmentDraftHandler(data: AssessmentListItemResponse | undefined) {
    if (!data || typeof data.slug === 'undefined') return;

    if (data.assessmentSubType === AssessmentSubType.RatingAssessment) {
      keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementEditRatingAssessment.replace(':companySlug', params.companySlug!).replace(':assessmentSlug', data.slug)}`);
      return;
    }

    if (data.assessmentSubType === AssessmentSubType.GroupAssessment) {
      keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementEditGroupAssessment.replace(':companySlug', params.companySlug!).replace(':assessmentSlug', data.slug)}`);
      return;
    } else if (data.assessmentSubType === AssessmentSubType.QuestionnaireAssessment) {
      keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementEditQuestionnaireAssessment.replace(':companySlug', params.companySlug!).replace(':assessmentSlug', data.slug)}`);
      return;
    } else if (data.assessmentSubType === AssessmentSubType.FOPAssessment) {
      keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementEditFOPAssessment.replace(':companySlug', params.companySlug!).replace(':assessmentSlug', data.slug)}`);
      return;
    }
  }

  async function onClickViewAssessmentOverview(data: AssessmentListItemResponse | undefined) {
    if (!data || typeof data.slug === 'undefined') return;

    if (data.assessmentSubType === AssessmentSubType.RatingAssessment && (data.assessmentStatus === AssessmentStatus.Active || data.assessmentStatus === AssessmentStatus.Closed || data.assessmentStatus === AssessmentStatus.Archived)) {
      keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementViewRatingAssessment.replace(':companySlug', params.companySlug!).replace(':assessmentSlug', data.slug)}`);
      return;
    } else if (data.assessmentSubType === AssessmentSubType.RatingAssessment && data.assessmentStatus === AssessmentStatus.Draft) {
      keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementEditRatingAssessment.replace(':companySlug', params.companySlug!).replace(':assessmentSlug', data.slug)}`);
      return;
    }
    if (data.assessmentSubType !== AssessmentSubType.RatingAssessment && (data.assessmentStatus === AssessmentStatus.Draft || data.assessmentStatus === AssessmentStatus.Archived)) {
      keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementEditGroupAssessment.replace(':companySlug', params.companySlug!).replace(':assessmentSlug', data.slug)}`);
      return;
    } else if (data.assessmentSubType !== AssessmentSubType.RatingAssessment && (data.assessmentStatus === AssessmentStatus.Active || data.assessmentStatus === AssessmentStatus.Closed)) {
      keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementAssignees.replace(':companySlug', params.companySlug!).replace(':assessmentSlug', data.slug)}`);
      return;
    }
  }

  async function onClickArchiveDraftAssessmentHandler(data: AssessmentListItemResponse | undefined) {
    dispatch({ type: store.AssessmentManagementActionType.SetArchiveConfirmationModalOpen, payload: { open: true, data: data } });
  }

  async function onClickCompleteHandler(data: AssessmentListItemResponse | undefined) {
    dispatch({ type: store.AssessmentManagementActionType.SetCloseConfirmationModalOpen, payload: { open: true, data: data } });
  }

  async function onClickViewAssigneesHandler(data: AssessmentListItemResponse | undefined) {
    if (!data || typeof data.slug === 'undefined') return;

    keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementAssignees.replace(':companySlug', params.companySlug!).replace(':assessmentSlug', data.slug)}`);
  }

  function onClickCancelManagingColumns() {
    dispatch({ type: store.AssessmentManagementActionType.ManageColumns, payload: { open: false, reset: false } });
  }

  function onClickCancelArchiveModal() {
    dispatch({ type: store.AssessmentManagementActionType.SetArchiveConfirmationModalOpen, payload: { open: false } });
    dispatch({ type: store.AssessmentManagementActionType.SetCloseConfirmationModalOpen, payload: { open: false } });
  }

  function onClickCancelCloseModal() {
    dispatch({ type: store.AssessmentManagementActionType.SetCloseConfirmationModalOpen, payload: { open: false } });
  }
  function onClickResetManagingColumns() {
    dispatch({ type: store.AssessmentManagementActionType.ManageColumns, payload: { open: true, reset: true } });
  }

  function onClickSubmitManagingColumns(selection: ColumnConfiguration[]) {
    dispatch({ type: store.AssessmentManagementActionType.EditColumnConfiguration, payload: selection });
    dispatch({ type: store.AssessmentManagementActionType.ManageColumns, payload: { open: false } });
  }

  function getSelectedFilters(): SelectedFilterOption[] {
    const filtersSelected = [];
    if (state.searchParams.assessmentSubType !== undefined) {
      filtersSelected.push({
        name: ASSESSMENT_SUB_TYPE_OPTIONS.find(option => option.value === state.searchParams.assessmentSubType?.toString())?.label || 'Assessment type',
        filterType: FilterType.AssessmentSubType,
        slug: `${FilterType.AssessmentSubType}${state.searchParams.assessmentSubType}`,
      });
    }

    if (state.searchParams.assessmentStatuses !== undefined && state.searchParams.assessmentStatuses.length > 0) {
      state.searchParams.assessmentStatuses?.forEach(status => {
        filtersSelected.push({
          name: STATUS_OPTIONS.find(option => option.value === status.toString())?.label || 'Status',
          filterType: FilterType.AssessmentStatuses,
          slug: `${FilterType.AssessmentStatuses}${status}`,
        });
      });
    }
    return filtersSelected;
  }

  return (
    <React.Fragment>
      <div className="wrapper stack" style={{ gap: 16 }}>
        <PageHeader type="other" breadcrumbs={[{ name: 'Assessment Management', url: location.pathname }]} title="Assessment Management" subtitle="Create and customise assessments by selecting skills, roles, and criteria for evaluation.">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 16 }}>
            <SearchBar
              loading={isSearching}
              label="Search"
              onInput={async event => {
                setIsSearching(true);
                const search = (event.target as HTMLInputElement).value;
                dispatch({ type: store.AssessmentManagementActionType.SetSearchParams, payload: { ...state.searchParams, search, page: 1 } });
                await updateData({ ...state.searchParams, search, page: 1 });
                setIsSearching(false);
              }}
            />

            <Filters
              isActive={!!(state.searchParams.assessmentSubType || state.searchParams.assessmentStatuses?.length)}
              onClickClear={async () => {
                setIsLoading(true);
                const updatedSearchParams = { ...state.searchParams, assessmentSubType: undefined, assessmentStatuses: undefined, page: 1 };
                setSelectedFilters([]);
                dispatch({ type: store.AssessmentManagementActionType.SetSearchParams, payload: updatedSearchParams });
                await updateData(updatedSearchParams);
                setIsLoading(false);
              }}
              onClickApply={async () => {
                if (state.searchParams.assessmentSubType === undefined && (!Array.isArray(state.searchParams.assessmentStatuses) || !state.searchParams.assessmentStatuses.every(status => typeof status === 'number'))) return;
                setIsLoading(true);
                setSelectedFilters(getSelectedFilters());
                await updateData({ ...state.searchParams, page: 1 });
                setIsLoading(false);
              }}
            >
              <DropdownSelect
                label="Assessment type"
                value={state.searchParams.assessmentSubType?.toString()}
                options={ASSESSMENT_SUB_TYPE_OPTIONS}
                onChange={event => {
                  if (isNaN(parseInt(event.target.value))) return;

                  dispatch({ type: store.AssessmentManagementActionType.SetSearchParams, payload: { ...state.searchParams, assessmentSubType: parseInt(event.target.value) } });
                }}
              />

              <DropdownMenu
                multiple
                label="Status"
                value={state.searchParams.assessmentStatuses?.toString()}
                options={STATUS_OPTIONS}
                onChange={event => {
                  if (isNaN(parseInt(event.target.value))) return;

                  dispatch({ type: store.AssessmentManagementActionType.SetSearchParams, payload: { ...state.searchParams, assessmentStatuses: event.target.value.includes(',') ? event.target.value.split(',').map(value => parseInt(value)) : [parseInt(event.target.value)] } });
                }}
              />
            </Filters>

            <SortBy
              value={state.searchParams.sortBy}
              options={assessmentSortByOptions.filter(option => option.value !== SortByOption.RecentlyActivated)}
              onChange={async event => {
                setIsLoading(true);
                const sortByType = event.target.value;
                dispatch({ type: store.AssessmentManagementActionType.SetSearchParams, payload: { ...state.searchParams, sortBy: sortByType as SortByOption } });
                await updateData({ ...state.searchParams, sortBy: sortByType as SortByOption });
                setIsLoading(false);
              }}
            />

            <div style={{ marginLeft: isMobile ? '' : 'auto' }}>
              <Button fullWidthMobile type="button" square theme="dark" onClick={() => keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementCreate.replace(':companySlug', companyVariables.slug!)}`)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <AddTwoToneIcon />
                  Create assessment
                </div>
              </Button>
            </div>
          </div>

          <FiltersOutlet
            selectedFilters={selectedFilters}
            removeFilter={async filter => {
              setIsLoading(true);
              setSelectedFilters(selectedFilters => selectedFilters.filter(f => f.slug !== filter.slug));
              const updatedSearchParams = {
                ...state.searchParams,
                [filter.filterType]: filter.filterType === FilterType.AssessmentSubType ? undefined : state.searchParams.assessmentStatuses?.length === 1 ? undefined : state.searchParams.assessmentStatuses?.filter(status => `${FilterType.AssessmentStatuses}${status}` !== filter.slug),
                page: 1,
              };
              dispatch({ type: store.AssessmentManagementActionType.SetSearchParams, payload: updatedSearchParams });
              await updateData(updatedSearchParams);
              setIsLoading(false);
            }}
          />
        </PageHeader>

        <SkeletonOverlay isLoading={isLoading} width="100%" height="750px">
          {state.data?.assessments && state.data.assessments.length > 0 ? (
            <AssessmentManagementTable
              totalPages={state.data?.totalPages ?? 1}
              loadingSkillAssessments={loadingSkillAssessments}
              skillAssessments={state.data?.assessments}
              onConfigClicked={() => dispatch({ type: store.AssessmentManagementActionType.ManageColumns, payload: { open: true } })}
              searchParams={state.searchParams}
              onPageChangeHandler={onPageChangeHandler}
              columnConfiguration={state.columnConfiguration}
              onClickManageAssessmentDraftHandler={onClickManageAssessmentDraftHandler}
              onClickViewAssessmentOverview={onClickViewAssessmentOverview}
              onClickArchiveDraftAssessmentHandler={onClickArchiveDraftAssessmentHandler}
              onClickCompleteHandler={onClickCompleteHandler}
              onClickViewAssigneesHandler={onClickViewAssigneesHandler}
            />
          ) : (
            <EmptyState
              title="There are no skill assessments yet"
              buttonElements={[
                <Button key="Create One" type="button" onClick={() => keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementCreate.replace(':companySlug', companyVariables.slug!)}`)}>
                  Create One
                </Button>,
              ]}
            />
          )}
        </SkeletonOverlay>
      </div>

      <Panel open={!!state.managingColumns} onClose={onClickCancelManagingColumns}>
        <ColumnConfiguratorWidget onReset={onClickResetManagingColumns} onCancel={onClickCancelManagingColumns} onSave={onClickSubmitManagingColumns} columns={state.columnConfiguration} />
      </Panel>

      <Modal open={!!state.isArchiveModalOpen} onClose={onClickCancelArchiveModal}>
        <ConfirmationModalLayout
          titleSize="medium"
          onClickCancel={onClickCancelArchiveModal}
          onClickSubmit={async () => {
            if (state.archiveSkillAssessmentData?.slug) {
              setLoadingSkillAssessments(skillAssessment => [...skillAssessment, String(state.archiveSkillAssessmentData?.assessmentId)]);
              await actions.archiveAssessment(state.archiveSkillAssessmentData?.slug);
              dispatch({ type: store.AssessmentManagementActionType.SetArchiveConfirmationModalOpen, payload: { open: false, data: undefined } });
              updateData(state.searchParams);
              setLoadingSkillAssessments(skillAssessments => [...skillAssessments.filter(sid => String(state.archiveSkillAssessmentData?.assessmentId) !== sid)]);
            }
          }}
          title="Are you sure you want to archive this assessment?"
          cancelButtonText="Cancel"
          submitButtonText="Yes, archive"
        />
      </Modal>

      <Modal open={!!state.isCloseModalOpen} onClose={onClickCancelCloseModal}>
        <ConfirmationModalLayout
          onClickCancel={onClickCancelCloseModal}
          onClickSubmit={async () => {
            if (state.closeSkillAssessmentData?.slug) {
              setLoadingSkillAssessments(skillAssessment => [...skillAssessment, String(state.closeSkillAssessmentData?.assessmentId)]);
              await actions.completeAssessment(state.closeSkillAssessmentData?.slug);
              dispatch({ type: store.AssessmentManagementActionType.SetCloseConfirmationModalOpen, payload: { open: false, data: undefined } });
              updateData(state.searchParams);
              setLoadingSkillAssessments(skillAssessments => [...skillAssessments.filter(sid => String(state.closeSkillAssessmentData?.assessmentId) !== sid)]);
            }
          }}
          title="Are you sure you want to close this assessment?"
          subtitle="This will close the active assessment. People who have not completed the assessment will no longer be able to complete it. Scores will not be affected."
          cancelButtonText="Cancel"
          submitButtonText="Yes, close"
        />
      </Modal>
    </React.Fragment>
  );
}
